import { useState, useEffect, useCallback } from 'react'
import Divider from '@mui/material/Divider';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { updateStore } from '../../../utils/database';
import { useOutletContext } from "react-router-dom";
import { isValidText, isValidEmail } from '../../../utils/strings';
import Confirmation from '../../../components/Confirmation';
import { changeEmail, changePassword } from '../../../utils/firebase';
import EasySnackBar from '../../../components/EasySnackBar';
import Styles from '../../../styles/AppIndex.module.css'

export default function Settings() {
    const [userAuth, userData, userMenu, updateUserData, updateUserMenu, setPreventNavigation] = useOutletContext()

    const [email, setEmail] = useState('')
    const [repeatedEmail, setRepeatedEmail] = useState('')

    const [password, setPassword] = useState('')
    const [repeatedPassword, setRepeatedPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)

    const [error, setError] = useState({ length: true, validText: true, repeated: true, validEmail: true })

    const [changeAction, setChangeAction] = useState('')
    const [currentPassword, setCurrentPassword] = useState('')

    const [storeStateConfirmation, setStoreStateConfirmation] = useState(false)

    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    useEffect(() => {
        setError(
            prev => {
                return ({
                    length: password.length < 8,
                    validText: !isValidText(password),
                    repeated: password !== repeatedPassword,
                    validEmail: prev.validEmail
                })
            }
        )
    }, [password, repeatedPassword])

    useEffect(() => {
        setError(
            prev => {
                return ({
                    length: prev.length,
                    validText: prev.validText,
                    repeated: prev.repeated,
                    validEmail: !isValidEmail(email),
                    repeatedEmail: email !== repeatedEmail
                })
            }
        )
    }, [email, repeatedEmail])

    const storeHabilitationHandle = useCallback(() => {
        // update new values in the DB
        updateStore(userAuth, userData.menuid, { disabled: !userMenu.disabled })
        // get last values from DB
        updateUserMenu()
    }, [userAuth, userData, userMenu])

    const onEmailUpdate = useCallback(async () => {
        if (!error.validEmail && !error.repeatedEmail) {
            changeEmail(currentPassword, email)
                .then(() => setSuccessMessage('Email cambiado correctamente'),
                    (error) => {
                        if (error.code === 'auth/wrong-password') {
                            setErrorMessage('Contraseña incorrecta')
                        } else {
                            setErrorMessage('Algo salió mal, intenta nuevamente mas tarde')
                        }
                    })
        }

        setCurrentPassword('')
        setChangeAction('')
    }, [error, currentPassword, email])

    const onPasswordUpdate = useCallback(async () => {
        if (!error.length && !error.repeated && !error.validText) {
            changePassword(currentPassword, password)
                .then(() => setSuccessMessage('Contraseña cambiada correctamente'),
                    (error) => {
                        if (error.code === 'auth/wrong-password') {
                            setErrorMessage('Contraseña incorrecta')
                        } else {
                            setErrorMessage('Algo salió mal, intenta nuevamente mas tarde')
                        }
                    })
        }

        setCurrentPassword('')
        setChangeAction('')
    }, [error, currentPassword, password])

    return (
        <>
            {userAuth.providerData[0].providerId !== 'password' ? null :
                <>
                    <Stack spacing={2} alignItems='flex-start'>
                        <Typography variant='h4'>
                            Administrar cuenta
                        </Typography>

                        <Divider sx={{ width: '100%', m: 1 }} />

                        <Typography variant='h5'>
                            Cambiar email
                        </Typography>

                        <TextField
                            fullWidth
                            label="Nuevo email"
                            variant="outlined"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            type='email'
                            error={error.validEmail && email !== ''}
                        />

                        <TextField
                            fullWidth
                            label="Repetir nuevo email"
                            variant="outlined"
                            value={repeatedEmail}
                            onChange={e => setRepeatedEmail(e.target.value)}
                            type='email'
                            error={error.validEmail && email !== ''}
                        />

                        <div>
                            <Stack direction='row' sx={{ m: 1 }}>
                                {error.validEmail ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                <span style={{ color: error.validEmail ? '#D32F2F' : '#2E7D32' }}> Debes ingresar un email valido</span>
                            </Stack>
                            <Stack direction='row' sx={{ m: 1 }}>
                                {error.repeatedEmail ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                <span style={{ color: error.repeatedEmail ? '#D32F2F' : '#2E7D32' }}> Los emails deben coincidir</span>
                            </Stack>
                        </div>

                        <Button variant='contained' fullWidth disabled={error.validEmail || error.repeatedEmail}
                            onClick={() => setChangeAction('email')}
                        >
                            Cambiar email
                        </Button>

                        <Divider sx={{ width: '100%', m: 1 }} />

                        <Typography variant='h5'>
                            Cambiar contraseña
                        </Typography>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Nueva contraseña</InputLabel>
                            <OutlinedInput
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                fullWidth

                                type={showPassword ? 'text' : 'password'}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={setShowPassword.bind(this, prev => !prev)}
                                            onMouseDown={e => e.preventDefault()}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Nueva contraseña"
                            />
                        </FormControl>

                        <FormControl fullWidth variant="outlined">
                            <InputLabel >Repetir nueva contraseña</InputLabel>
                            <OutlinedInput
                                value={repeatedPassword}
                                onChange={e => setRepeatedPassword(e.target.value)}
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={setShowPassword.bind(this, prev => !prev)}
                                            onMouseDown={e => e.preventDefault()}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Repetir nueva contraseña"
                            />
                        </FormControl>

                        <div>
                            <Stack direction='row' sx={{ m: 1 }}>
                                {error.length ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                <span style={{ color: error.length ? '#D32F2F' : '#2E7D32' }}> La nueva contraseña debe tener al menos 8 caracteres</span>
                            </Stack>

                            <Stack direction='row' sx={{ m: 1 }}>
                                {error.validText ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                <span style={{ color: error.validText ? '#D32F2F' : '#2E7D32' }}> Solo puede incluir numeros, letras o simbolos</span>
                            </Stack>

                            <Stack direction='row' sx={{ m: 1 }}>
                                {error.repeated ? <CloseIcon color='error' /> : <CheckIcon color='success' />}
                                <span style={{ color: error.repeated ? '#D32F2F' : '#2E7D32' }}> Las contraseñas deben coincidir</span>
                            </Stack>
                        </div>

                        <Button
                            variant='contained'
                            fullWidth
                            disabled={error.length || error.repeated || error.validText}
                            onClick={() => setChangeAction('password')}
                        >
                            Cambiar contraseña
                        </Button>

                        <Divider sx={{ width: '100%', m: 1 }} />

                        <Typography variant='h5'>
                            Deshabilitar comercio
                        </Typography>

                        <Typography variant='body1'>
                            Puedes deshabilitar tu tienda temporalmente.
                            <br />
                            Tus clientes verán un mensaje diciendo que tu comercio se encuentra cerrado temporalmente
                        </Typography>

                    </Stack>

                    <Dialog open={changeAction !== ''} onClose={() => setChangeAction('')}>
                        <DialogTitle>Ingresa tu contraseña actual</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Para realizar este tipo de cambios en tu cuenta es necesario que ingreses tu contraseña
                            </DialogContentText>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="name"
                                label="Contraseña"
                                type="password"
                                fullWidth
                                variant="standard"
                                value={currentPassword}
                                onChange={e => setCurrentPassword(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button color='error' onClick={() => setChangeAction('')}>Cancelar</Button>
                            <Button variant='contained'
                                onClick={() => { changeAction === 'email' ? onEmailUpdate() : onPasswordUpdate() }}
                            >Confirmar</Button>
                        </DialogActions>
                    </Dialog>
                </>
            }

            <Stack spacing={2} alignItems='flex-start'>
                {!userMenu ? null : userMenu.disabled ?
                    <>
                        <Typography variant='h6'>
                            Tu comercio actualmente se encuentra <span
                                className={`${Styles.storeDisabled} ${Styles.storeState}`}>DESACTIVADO</span>
                        </Typography>
                        <Button color='success' variant='contained' onClick={() => setStoreStateConfirmation(true)}>
                            Activar comercio
                        </Button>
                    </> :
                    <>
                        <Typography variant='h6'>
                            Tu comercio actualmente se encuentra <span
                                className={`${Styles.storeActive} ${Styles.storeState}`} >ACTIVO</span>
                        </Typography>
                        <Button color='error' variant='contained' onClick={() => setStoreStateConfirmation(true)}>
                            Desactivar comercio
                        </Button>
                    </>
                }
            </Stack>

            <Confirmation
                action={() => storeHabilitationHandle()}
                open={storeStateConfirmation}
                setOpen={setStoreStateConfirmation}
                title={"Estas seguro que deseas " + (userMenu.disabled ? 'ACTIVAR ' : 'DESACTIVAR ') + 'tu comercio?'}
                description={"Puedes revertir este estado cuando quieras"}
                actionMsg={userMenu.disabled ? 'Activar' : 'Desactivar'}
                rejectMsg={"Cancelar"}
            />

            <EasySnackBar
                severity='error'
                key='1'
                message={errorMessage}
                setMessage={setErrorMessage}
            />

            <EasySnackBar
                severity='success'
                key='2'
                message={successMessage}
                setMessage={setSuccessMessage}
            />
        </>
    )
}